import { useCallback, useMemo, useRef, useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import { formatMoney, getDurationInDays, isAdvertiser } from '~/helpers'
import useQueryParams from '~/hooks/useQuery'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import {
  listCampaignPlacesKey,
  listCampaignPlacesService
} from '~/modules/retailMedia/services/campaign-places'
import { getMinCostService } from '~/modules/retailMedia/services/campaign/min-cost'

import BalanceByPublisher from '../../../BalanceByPublisher'
import { useCheckingAccountIdForBalance } from '../../hooks/useCheckingAccountIdForBalance'
import totalBudgetComponents from './Budget'
import { BudgetSettingsData } from './Budget/Standard/hooks'
import { requestMinCostValidator } from './validator'

const Investment = ({
  formRef,
  formData,
  campaignId,
  initialData,
  requiredData,
  setRequiredData,
  isTotalBudgetEditable,
  adCostType,
  formIsReadyToSubmit
}: InvestmentProps) => {
  const searchParams = useQueryParams()
  const id = searchParams.get('rmid')
  const { t } = useAPPTranslation()
  const checkingAccountId = useCheckingAccountIdForBalance()
  const [totalBudgetQuery, setTotalBudgetQuery] = useState(null)
  const [budgetSettingsData, setBudgetSettingsData] =
    useState<BudgetSettingsData>({
      budgetDaily: null,
      budgetCpm: null
    })
  const campaignData = formData || initialData

  const temporaryValidationID = '5d16ad2c-46e3-4ef5-98bb-83f6c4886882' //TODO: remove this line in the future

  const QUERY_KEY = [listCampaignPlacesKey, campaignId, 1, 1]

  const { data: placesLength } = useQuery({
    enabled: !!campaignId,
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const { data } = await listCampaignPlacesService({
        campaignId: campaignId as string,
        page: 1,
        quantity: 1,
        count: true
      })
      return data.total
    }
  })

  /** Fake asset */
  const fakeAsset = useMemo(
    () => ({
      video: {
        assets: [
          {
            type: 'video',
            url: 'https://www.w3schools.com/html/mov_bbb.mp4',
            ...(requiredData?.uploadedMediaToTable?.[0]?.dimensions || {})
          }
        ]
      }
    }),
    [requiredData?.uploadedMediaToTable]
  )

  const assets = useMemo(
    () => (campaignData?.video?.assets ? campaignData.video : fakeAsset),
    [campaignData?.video, fakeAsset]
  )

  // format data to be used in the request
  const bodyRequestMinCost = useCallback(() => {
    const body: DTOCreateDigitalSignageCampaignResponse = {
      ...campaignData,
      ...assets,
      cost_type: adCostType,
      settings: {
        ...campaignData?.settings,
        places: campaignData.places || campaignData.settings.places
      }
    }
    if (totalBudgetQuery) {
      body.total_budget = totalBudgetQuery
    }
    return body
  }, [campaignData, assets, totalBudgetQuery, adCostType])

  // On create
  const dataIsValid = useMemo(() => {
    const hasPlaces = !isNaN(placesLength)
    const isDynamicCostType =
      (adCostType as CampaignPermissionsCostType) ===
      ('dynamic' as CampaignPermissionsCostType)

    if (campaignId && isDynamicCostType && hasPlaces) {
      return true
    }

    if (campaignId) {
      return false
    }

    try {
      requestMinCostValidator.validateSync(
        { ...campaignData, ...fakeAsset },
        { abortEarly: false }
      )
      return true
    } catch (error) {
      return false
    }
  }, [campaignData, campaignId, placesLength, adCostType, fakeAsset])

  const { data, isLoading } = useQuery({
    enabled: dataIsValid,
    queryKey: [
      'minCosts',
      campaignData?.start_at,
      campaignData?.end_at,
      campaignData?.settings?.places,
      campaignData?.publisher_id,
      assets,
      totalBudgetQuery,
      campaignData?.settings?.cost_type as CampaignPermissionsCostType
    ],
    queryFn: async () => {
      const response = await getMinCostService({
        body: bodyRequestMinCost()
      })

      return response?.data
    }
  })

  const hasToFillFields = useMemo(
    () =>
      !campaignData?.start_at ||
      !campaignData?.end_at ||
      (!campaignData?.settings?.places?.length &&
        !campaignData?.settings?.use_all_places &&
        !placesLength) ||
      !requiredData?.isMediaValid,
    [
      campaignData?.start_at,
      campaignData?.end_at,
      campaignData?.settings?.places?.length,
      campaignData?.settings?.use_all_places,
      placesLength,
      requiredData?.isMediaValid
    ]
  )

  const previousTotalBudgetQuery = useRef(totalBudgetQuery)
  const isMinMaxBudgetLoading = useMemo(() => {
    const isTotalBudgetChanged =
      previousTotalBudgetQuery.current !== totalBudgetQuery
    previousTotalBudgetQuery.current = totalBudgetQuery

    return isLoading && !hasToFillFields && !isTotalBudgetChanged
  }, [isLoading, totalBudgetQuery, hasToFillFields])

  const values = useMemo(() => {
    const startDate = campaignData?.start_at
    const endDate = campaignData?.end_at
    const hasDate = startDate && endDate
    const getDuration = () =>
      getDurationInDays({
        startDate,
        endDate,
        returnType: 'number'
      }) as number

    const duration = hasDate ? getDuration() : null

    const dailyBudgetValue = isNaN(Number(budgetSettingsData?.budgetDaily))
      ? null
      : Number(budgetSettingsData?.budgetDaily)

    const totalPlaces =
      campaignData?.settings?.places?.length ||
      data?.total_places ||
      placesLength

    const dailyCost = isNaN(data?.daily_cost)
      ? null
      : formatMoney(data?.daily_cost)

    const totalDailyCost = campaignData?.settings?.daily_cost
      ? formatMoney(campaignData?.settings?.daily_cost)
      : isNaN(data?.daily_cost) || data?.daily_cost === 0
        ? null
        : formatMoney(data?.daily_cost)

    const minDailyBudgetValue = isNaN(data?.min_daily_budget)
      ? null
      : data?.min_daily_budget

    const totalCostValue = !isNaN(data?.total_cost) ? data?.total_cost : null

    const cpmValue = isNaN(Number(data?.cpm)) ? null : Number(data?.cpm)

    const dailyReproductionsValue =
      dailyBudgetValue && cpmValue
        ? Math.floor((dailyBudgetValue / cpmValue) * 1000)
        : null

    let totalCost = campaignData?.settings?.daily_cost
      ? formatMoney(campaignData?.settings?.daily_cost * duration)
      : !isNaN(totalCostValue)
        ? formatMoney(totalCostValue)
        : null

    if (campaignId === temporaryValidationID) {
      totalCost = formatMoney(800000)
    }

    return {
      duration,
      totalPlaces,
      dailyCost,
      totalDailyCost,
      totalCostValue,
      totalCost,
      minDailyBudgetValue,
      cpmValue,
      dailyReproductionsValue
    }
  }, [
    campaignData?.start_at,
    campaignData?.end_at,
    campaignData?.settings?.places?.length,
    campaignData?.settings?.daily_cost,
    budgetSettingsData?.budgetDaily,
    placesLength,
    data?.daily_cost,
    data?.min_daily_budget,
    data?.total_cost,
    data?.total_places,
    data?.cpm,
    campaignId
  ])

  const totalBudget = useMemo(
    () =>
      isNaN(campaignData?.total_budget) ||
      Number(campaignData?.total_budget) === 0
        ? null
        : formatMoney(campaignData?.total_budget),
    [campaignData?.total_budget]
  )

  const reproductions = useMemo(
    () => ({
      canPurchaseReproductions: data?.can_purchase_reproductions,
      purchaseReproductions: data?.purchase_reproductions,
      totalReproductions: data?.total_reproductions,
      reproductionCost: data?.reproduction_cost,
      maxTotalBudget:
        (data?.total_reproductions ?? 0) * (data?.reproduction_cost ?? 0)
    }),
    [
      data?.can_purchase_reproductions,
      data?.purchase_reproductions,
      data?.total_reproductions,
      data?.reproduction_cost
    ]
  )

  const Title = useMemo(() => {
    return (
      <div className="d-flex align-items-center justify-content-between gap-2">
        <span>{t('rm:campaign.create.Investment.title')}</span>
        {!!formRef && (
          <div className="fw-normal">
            <BalanceByPublisher
              id={id}
              idAccountType={isAdvertiser() ? 'industry' : 'grocery'}
              checkingAccountId={checkingAccountId}
            />
          </div>
        )}
      </div>
    )
  }, [t, formRef, id, checkingAccountId])

  const { BudgetComponent } = useMemo(() => {
    let key = 'dynamic' as keyof typeof totalBudgetComponents
    const adCostTypeKey = adCostType as keyof typeof totalBudgetComponents

    if (!adCostType) {
      const deprecatedBudgetComponentKey = isTotalBudgetEditable
        ? 'total_budget'
        : 'dynamic'
      key = deprecatedBudgetComponentKey
    } else {
      key = adCostTypeKey
    }
    return {
      BudgetComponent:
        totalBudgetComponents?.[key] || totalBudgetComponents?.['dynamic']
    }
  }, [adCostType, isTotalBudgetEditable])

  return (
    <BudgetComponent
      formRef={formRef}
      Title={Title}
      values={values}
      setRequiredData={setRequiredData}
      setTotalBudgetQuery={setTotalBudgetQuery}
      hasUploadedMedia={requiredData?.isMediaValid}
      isLoading={isLoading}
      reproductions={reproductions}
      isMinMaxBudgetLoading={isMinMaxBudgetLoading}
      hasToFillFields={hasToFillFields}
      totalBudget={totalBudget}
      isTemporaryValidationID={campaignId === temporaryValidationID}
      isForm={!!formRef}
      minCostCPM={data?.min_cpm}
      publisherId={campaignData?.publisher_id}
      campaignData={campaignData}
      formIsReadyToSubmit={formIsReadyToSubmit}
      budgetSettingsData={budgetSettingsData}
      setBudgetSettingsData={setBudgetSettingsData}
    />
  )
}

export default Investment
